import { calculateRiskScore } from '@common/utils/calculateRiskScore';
import { Avatar, Stack, Typography } from '@mui/material';
import { aponoColors } from '@utils';

interface HexagonProps {
  color: string;
  letter: string;
}

export const LOW_RISK_THRESHOLD = 3;
export const MEDIUM_RISK_THRESHOLD = 6;

const Hexagon: React.FC<HexagonProps> = ({ color, letter }) => {
  const hexagonStyle: React.CSSProperties = {
    backgroundColor: color,
    color: 'white',
    width: '20px',
    height: '20px',
    lineHeight: '12px',
    textAlign: 'center',
    clipPath: 'polygon(50% 0%, 90% 20%, 90% 80%, 50% 100%, 10% 80%, 10% 20%)',
  };

  return (
    <Avatar style={hexagonStyle}>
      <Typography variant="helperText">{letter}</Typography>
    </Avatar>
  );
};

export function RiskScore({
  resourceRiskLevel,
  permissionRiskLevel,
  withRiskScore = true,
}: {
  resourceRiskLevel: string;
  permissionRiskLevel: string;
  withRiskScore?: boolean;
}) {
  const { riskScore, maxRiskScore } = calculateRiskScore(resourceRiskLevel, permissionRiskLevel);

  const color = getRiskLevelColor(riskScore);
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Hexagon letter={resourceRiskLevel.charAt(0).toUpperCase()} color={color} />
      {withRiskScore && (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="subtitle2">{riskScore}</Typography>
          <Typography variant="helperText" color="text.muted">
            / {maxRiskScore}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}

export function getRiskLevelColor(riskScore: number) {
  if (riskScore === 0) {
    // unknown risk score or now risk score - empty state
    return aponoColors.neutral[500];
  } else if (riskScore <= LOW_RISK_THRESHOLD) {
    return aponoColors.success[500];
  } else if (riskScore <= MEDIUM_RISK_THRESHOLD) {
    return aponoColors.tertiary[500];
  } else {
    return aponoColors.error[500];
  }
}
